import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Stethoscope, Download, ArrowRight, CheckCircle2, ShieldAlert, KeyRound,
  ClipboardList, Layers, FileText, Clock, Mic,
} from "lucide-react";
import ChangePasswordDialog from "../components/ChangePasswordDialog";

const OUTCOMES = ["Normal", "Mild", "Moderate", "High", "Inconclusive"];
const BAND_TONE = {
  Normal: "bg-primary/10 text-primary border-primary/30",
  Mild: "bg-secondary/30 text-primary border-secondary",
  Moderate: "bg-[hsl(var(--chart-4)/0.25)] text-foreground border-[hsl(var(--chart-4))]",
  High: "bg-destructive/10 text-destructive border-destructive/40",
  Inconclusive: "bg-muted text-muted-foreground border-border/60",
};

// Format an ISO timestamp for the doctor UI. Falls back to "—" if the value
// is missing so the label always renders.
function fmtTimestamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const SECTION_LABEL = {
  MENTAL_HEALTH: "Mental Health",
  BRAIN_HEALTH: "Brain Health",
  ADDICTIONS: "Addictions",
  COGNITIVE_HEALTH: "Brain Health",
  INTOXICATION: "Addictions",
  NEURODEGENERATIVE: "Brain Health",
  SUBSTANCE_ABUSE: "Addictions",
};

// Per-instrument review form — one card per questionnaire the respondent
// completed. Doctor's observation is tied to (case_id, instrument_id) so the
// audit trail matches the respondent's unique id + the specific instrument.
function InstrumentReviewCard({ caseId, instrument, priorReviews, onReviewed }) {
  const [outcome, setOutcome] = useState(instrument.band && OUTCOMES.includes(instrument.band) ? instrument.band : "Normal");
  const [notes, setNotes] = useState("");
  const [blinded, setBlinded] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/doctor/cases/${caseId}/review`, {
        outcome,
        notes: notes.trim() || null,
        blinded_to_llm: blinded,
        instrument_id: instrument.instrument_id,
      });
      toast.success(`${instrument.title} · observation saved`);
      setNotes("");
      onReviewed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sectionLbl = SECTION_LABEL[instrument.section] || instrument.section || "";
  const testIdBase = `doctor-inst-${instrument.instrument_id}`;

  return (
    <Card className="mano-card" data-testid={`${testIdBase}-card`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/60 pb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <ClipboardList size={11} /> {sectionLbl || instrument.instrument_id}
            </div>
            <div className="font-display font-semibold text-lg mt-1" data-testid={`${testIdBase}-title`}>
              {instrument.title}
            </div>
            {instrument.label && (
              <div className="text-xs text-muted-foreground mt-0.5">{instrument.label}</div>
            )}
            <div className="text-[11px] text-muted-foreground mt-1.5 inline-flex items-center gap-1"
                 data-testid={`${testIdBase}-submitted-at`}>
              <Clock size={11} /> Sample given · <span className="font-medium">{fmtTimestamp(instrument.submitted_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {instrument.band && (
              <Badge className={"border text-xs " + (BAND_TONE[instrument.band] || "")}
                     data-testid={`${testIdBase}-band`}>
                {instrument.band}
              </Badge>
            )}
            {instrument.score !== null && instrument.score !== undefined && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Score</div>
                <div className="font-display text-xl leading-none" data-testid={`${testIdBase}-score`}>
                  {instrument.score}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm font-medium flex items-center gap-2">
            <ShieldAlert size={14} className="text-primary" /> Doctor's observation for this instrument
          </div>
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <input type="checkbox" checked={blinded} onChange={(e) => setBlinded(e.target.checked)}
                   data-testid={`${testIdBase}-blinded`} />
            Blinded to LLM
          </label>
        </div>

        <div>
          <Label>Outcome</Label>
          <RadioGroup value={outcome} onValueChange={setOutcome} className="mt-2 flex flex-wrap gap-2">
            {OUTCOMES.map((o) => (
              <label key={o}
                     htmlFor={`${testIdBase}-outcome-${o}`}
                     className={"cursor-pointer rounded-full border px-3 py-1 text-sm " +
                                (outcome === o ? "border-primary text-primary bg-primary/10" : "border-border/60")}
                     data-testid={`${testIdBase}-outcome-${o}`}>
                <RadioGroupItem id={`${testIdBase}-outcome-${o}`} value={o} className="sr-only" />
                {o}
              </label>
            ))}
          </RadioGroup>
        </div>

        <div>
          <Label htmlFor={`${testIdBase}-notes`}>Notes</Label>
          <Textarea id={`${testIdBase}-notes`} value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={3} placeholder="Instrument-specific clinical impression…"
                    data-testid={`${testIdBase}-notes`} />
        </div>

        <Button onClick={save} disabled={saving}
                className="rounded-full mano-lift"
                data-testid={`${testIdBase}-save`}>
          {saving ? "Saving…" : "Save observation"} <ArrowRight size={14} />
        </Button>

        {priorReviews.length > 0 && (
          <div className="pt-3 border-t border-border/50">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
              Prior observations · {priorReviews.length}
            </div>
            <div className="space-y-2">
              {priorReviews.map((r) => (
                <div key={r.review_id} className="rounded-lg border border-border/60 p-3 text-sm"
                     data-testid={`${testIdBase}-history-${r.review_id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge className={"border text-xs " + (BAND_TONE[r.outcome] || "")}>{r.outcome}</Badge>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {(r.created_at || "").replace("T", " ").slice(0, 16)}
                    </span>
                    {r.blinded_to_llm && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">· Blinded</span>
                    )}
                  </div>
                  {r.notes && <div className="text-muted-foreground">{r.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Case-level (overall) doctor impression — instrument_id omitted from the
// payload so the backend stores it as the case-level review (still surfaces
// on the aggregate XLSX "Cases" sheet under doctor_outcome/notes).
function OverallCaseSummaryCard({ caseId, priorReviews, onReviewed }) {
  const [outcome, setOutcome] = useState("Normal");
  const [notes, setNotes] = useState("");
  const [blinded, setBlinded] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/doctor/cases/${caseId}/review`, {
        outcome,
        notes: notes.trim() || null,
        blinded_to_llm: blinded,
        // instrument_id intentionally omitted → case-level overall review
      });
      toast.success("Overall case summary saved");
      setNotes("");
      onReviewed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mano-card border-primary/30" data-testid="doctor-overall-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 pb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-primary flex items-center gap-1">
              <FileText size={11} /> Overall case summary
            </div>
            <div className="font-display font-semibold text-lg mt-1">
              One combined clinical impression across every instrument
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Independent of the per-instrument observations below.
            </div>
          </div>
          <label className="text-xs text-muted-foreground flex items-center gap-2 shrink-0">
            <input type="checkbox" checked={blinded} onChange={(e) => setBlinded(e.target.checked)}
                   data-testid="doctor-overall-blinded" />
            Blinded to LLM
          </label>
        </div>

        <div>
          <Label>Outcome</Label>
          <RadioGroup value={outcome} onValueChange={setOutcome} className="mt-2 flex flex-wrap gap-2">
            {OUTCOMES.map((o) => (
              <label key={o}
                     htmlFor={`doctor-overall-outcome-${o}`}
                     className={"cursor-pointer rounded-full border px-3 py-1 text-sm " +
                                (outcome === o ? "border-primary text-primary bg-primary/10" : "border-border/60")}
                     data-testid={`doctor-overall-outcome-${o}`}>
                <RadioGroupItem id={`doctor-overall-outcome-${o}`} value={o} className="sr-only" />
                {o}
              </label>
            ))}
          </RadioGroup>
        </div>

        <div>
          <Label htmlFor="doctor-overall-notes">Combined impression / notes</Label>
          <Textarea id="doctor-overall-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={3} placeholder="Overall clinical impression across all instruments…"
                    data-testid="doctor-overall-notes" />
        </div>

        <Button onClick={save} disabled={saving}
                className="rounded-full mano-lift"
                data-testid="doctor-overall-save">
          {saving ? "Saving…" : "Save overall summary"} <ArrowRight size={14} />
        </Button>

        {priorReviews.length > 0 && (
          <div className="pt-3 border-t border-border/50">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
              Prior overall summaries · {priorReviews.length}
            </div>
            <div className="space-y-2">
              {priorReviews.map((r) => (
                <div key={r.review_id} className="rounded-lg border border-border/60 p-3 text-sm"
                     data-testid={`doctor-overall-history-${r.review_id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge className={"border text-xs " + (BAND_TONE[r.outcome] || "")}>{r.outcome}</Badge>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {(r.created_at || "").replace("T", " ").slice(0, 16)}
                    </span>
                    {r.blinded_to_llm && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">· Blinded</span>
                    )}
                  </div>
                  {r.notes && <div className="text-muted-foreground">{r.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CaseReviewPanel({ selected, onReviewed }) {
  const [detail, setDetail] = useState(null);

  const load = async () => {
    if (!selected) return;
    try {
      const r = await api.get(`/doctor/cases/${selected}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load case");
    }
  };

  useEffect(() => {
    setDetail(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Group prior doctor reviews by instrument for per-card history.
  const reviewsByInstrument = useMemo(() => {
    const map = {};
    for (const r of detail?.doctor_reviews || []) {
      const key = r.instrument_id || "__case__";
      (map[key] = map[key] || []).push(r);
    }
    return map;
  }, [detail]);

  const handleReviewed = async () => {
    await load();
    onReviewed();
  };

  if (!selected) return <div className="text-sm text-muted-foreground">Pick a case from the queue on the left.</div>;
  if (!detail) return <div className="text-sm text-muted-foreground">Loading case…</div>;

  const respondent = detail.respondent || {};
  const instruments = detail.instruments || [];
  const voiceSamples = detail.voice_samples || [];
  const overallReviews = reviewsByInstrument["__case__"] || [];

  return (
    <div className="space-y-4">
      <Card className="mano-card">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Respondent</div>
          <div className="mt-1 font-display text-lg" data-testid="doctor-panel-respondent-name">
            {respondent.name || respondent.email || detail.case?.respondent_id}
          </div>
          <div className="text-xs text-muted-foreground">
            {respondent.email} · {respondent.profession || "—"}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2"
               data-testid="doctor-panel-respondent-id">
            Respondent ID · <span className="font-mono">{detail.case?.respondent_id}</span>
          </div>

          {voiceSamples.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50" data-testid="doctor-panel-voice-samples">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1.5">
                <Mic size={11} /> Voice samples · {voiceSamples.length}
              </div>
              <ul className="space-y-1">
                {voiceSamples.slice(0, 5).map((s) => (
                  <li key={s.sample_id}
                      className="text-[11px] text-muted-foreground flex items-center gap-1.5"
                      data-testid={`doctor-panel-voice-${s.sample_id}`}>
                    <Clock size={10} />
                    <span className="font-medium text-foreground">
                      {fmtTimestamp(s.created_at)}
                    </span>
                    <span>· {s.sample_type || s.tag || s.prompt_id || "sample"}</span>
                    {s.duration_seconds ? (
                      <span className="text-muted-foreground">· {Math.round(s.duration_seconds)}s</span>
                    ) : null}
                  </li>
                ))}
                {voiceSamples.length > 5 && (
                  <li className="text-[10px] text-muted-foreground">…and {voiceSamples.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overall case-level summary — one combined impression across every
          instrument this respondent completed. Coexists with the per-instrument
          cards below (which are more granular). */}
      <OverallCaseSummaryCard caseId={selected}
                              priorReviews={overallReviews}
                              onReviewed={handleReviewed} />

      <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
        <Layers size={14} />
        <span data-testid="doctor-panel-inst-count">
          {instruments.length} instrument{instruments.length === 1 ? "" : "s"} completed · observe each one below
        </span>
      </div>

      {instruments.length === 0 ? (
        <Card className="mano-card">
          <CardContent className="p-6 text-sm text-muted-foreground" data-testid="doctor-panel-empty">
            This respondent has no completed questionnaires yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="doctor-panel-inst-list">
          {instruments.map((inst) => (
            <InstrumentReviewCard
              key={inst.instrument_id}
              caseId={selected}
              instrument={inst}
              priorReviews={reviewsByInstrument[inst.instrument_id] || []}
              onReviewed={handleReviewed}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DoctorDashboard() {
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);

  const load = () => {
    api.get("/doctor/cases").then((r) => setCases(r.data))
       .catch((e) => {
         if (e?.response?.status === 401 || e?.response?.status === 403) nav("/doctor/login");
         else toast.error("Could not load cases");
       });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/export/aggregate.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "manodayam_aggregate.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Aggregate export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container py-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-1">
              <Stethoscope size={14} /> Doctor console · independent review
            </div>
            <h1 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight" data-testid="doctor-dashboard-title">
              Case review queue
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setChangePwOpen(true)}
                    className="rounded-full"
                    data-testid="doctor-change-password-btn">
              <KeyRound size={13} /> Change password
            </Button>
            <Button onClick={exportXlsx} disabled={downloading}
                    className="rounded-full mano-lift"
                    data-testid="doctor-export-xlsx">
              <Download size={14} /> {downloading ? "Preparing…" : "One-click Excel export"}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-2" data-testid="doctor-case-list">
            {cases.length === 0 && (
              <div className="text-sm text-muted-foreground">No cases in the queue yet.</div>
            )}
            {cases.map((c) => (
              <button key={c.case_id} type="button"
                      onClick={() => setSelected(c.case_id)}
                      data-testid={`doctor-case-${c.case_id}`}
                      className={"w-full text-left rounded-lg border p-3 transition-colors " +
                                 (selected === c.case_id ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40")}>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium truncate">
                    {c.respondent_name || c.respondent_email || c.respondent_id?.slice(0, 8)}
                  </div>
                  {c.has_doctor_review && <CheckCircle2 size={14} className="text-primary shrink-0" />}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {c.status || "PENDING"} · {(c.created_at || "").slice(0, 10)}
                </div>
              </button>
            ))}
          </div>
          <div className="lg:col-span-2">
            <CaseReviewPanel selected={selected} onReviewed={load} />
          </div>
        </div>
      </main>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}
